const currencies: { [key: string]: string } = {
  USD: '$',
  EUR: '€',
  GBP: '£',
};

/**
 * Validates the unit and currency parameters.
 *
 * @param unit - The numeric value to be validated.
 * @param currency - The currency code to be validated.
 * @throws Throws an error if the unit is not a number or if the currency is unsupported.
 */
const validateParameters = (unit: number, currency: string): void => {
  if (typeof unit !== 'number' || isNaN(unit)) {
    throw new Error('Invalid unit: must be a number.');
  }
  if (!currencies[currency.toUpperCase()]) {
    throw new Error('Unsupported currency.');
  }
};

/**
 * Converts a price from minor units to major units.
 * Returns a currency string representation of the price.
 *
 * @param unit - The numeric value to be converted. This value is expected to be in minor units (e.g., cents).
 * @param currency - The currency code (e.g., 'USD', 'EUR', 'GBP').
 * @returns Returns the formatted currency string.
 * @throws Throws an error if the unit is not a number or if the currency is unsupported.
 *
 * @example
 * formatCurrencyToMajor(1000, 'EUR'); // Returns "€10.00"
 */
export const formatCurrencyToMajor = (unit: number, currency: string): string => {
  validateParameters(unit, currency);
  const _currency = currencies[currency.toUpperCase()];
  const formattedValue = (unit / 100).toFixed(2);
  return `${_currency}${formattedValue}`;
};

/**
 * Converts and formats a price from major units to minor units.
 * Returns a currency string representation of the price.
 *
 * @param unit - The numeric value to be converted. This value is expected to be in major units (e.g., dollars).
 * @param currency - The currency code (e.g., 'USD', 'EUR', 'GBP').
 * @returns Returns the formatted currency string.
 * @throws Throws an error if the unit is not a number or if the currency is unsupported.
 *
 * @example
 * formatCurrencyToMinor(10.00, 'GBP'); // Returns "£1000"
 */
export const formatCurrencyToMinor = (unit: number, currency: string): string => {
  validateParameters(unit, currency);
  const _currency = currencies[currency.toUpperCase()];
  return `${_currency}${(unit * 100).toFixed(0)}`;
};

/**
 * Converts a price to minor units.
 *
 * @param unit - The numeric value to be converted. This value is expected to be in major units (e.g., dollars).
 * @returns Returns the value in minor units.
 * @throws Throws an error if the unit is not a number.
 *
 * @example
 * formatPriceToMinor(10.00); // Returns 1000
 */
export const formatPriceToMinor = (unit: number): number => {
  if (typeof unit !== 'number' || isNaN(unit)) {
    throw new Error('Invalid unit: must be a number.');
  }
  return unit * 100;
};

/**
 * Converts a price to major units.
 *
 * @param unit - The numeric value to be converted. This value is expected to be in minor units (e.g., cents).
 * @returns Returns the value in major units.
 * @throws Throws an an error if the unit is not a number.
 *
 * @example
 * formatPriceToMajor(1000); // Returns 10.00
 */
export const formatPriceToMajor = (unit: number): number => {
  if (typeof unit !== 'number' || isNaN(unit)) {
    throw new Error('Invalid unit: must be a number.');
  }
  return unit / 100;
};
